/**
 * Calendar Query Hooks
 * Queries for fetching calendar data from Google and Microsoft
 */

import { useQuery } from "@tanstack/react-query";
import { useMemo, useRef, useEffect } from "react";
import { useAuth } from "../../useAuth.js";
import { apiGet } from "../../../lib/api-utils.js";
import { QUERY_CONFIG, ENDPOINTS } from "../config.js";
import { queryKeys } from "../query-keys.js";
import type { CalendarDataParams, CalendarEvent } from "../types.js";
import {
  buildQueryParams,
  isCalendarNotConnectedError,
  getErrorMessage,
  formatErrorResponse,
  logDebug,
  logWarning,
  logError,
  countEventsBySource,
} from "../utils.js";
import {
  parseGoogleCalendarEmails,
  parseMicrosoftCalendarEmails,
  normalizeGoogleCalendarEvents,
  normalizeMicrosoftCalendarEvents,
  normalizeMultipleCalendarEvents,
  logCalendarQueryRequest,
  logCalendarQueryResponse,
  logNormalizedCalendarEvents,
  logCombinedCalendarData,
} from "./utils.js";
import { useAccountStatus } from "../account/queries.js";
import { WTC_EMAIL_CALENDAR_SOURCE_ID } from "./constants.js";

/**
 * Get Google Calendar Events
 * Supports fetching from multiple Google calendars in parallel
 */
export const useGoogleCalendarData = (params: CalendarDataParams = {}) => {
  const { getAuthToken, user } = useAuth();
  const { data: accountStatus } = useAccountStatus();

  // Memoize the enabled condition to prevent infinite loops
  const enabled = useMemo(() => {
    const token = getAuthToken();
    const hasGoogleAccount =
      accountStatus &&
      (accountStatus.google.primary || accountStatus.google.secondary);
    return !!token && hasGoogleAccount;
  }, [getAuthToken, accountStatus]);

  // Parse selected calendars to get Google calendar emails
  const googleCalendarEmails = useMemo(() => {
    return parseGoogleCalendarEmails(params.selectedCalendars, accountStatus);
  }, [params.selectedCalendars, accountStatus]);

  // Create stable query key with selected calendars
  const queryKey = useMemo(() => {
    return queryKeys.calendar.googleWithParams(params);
  }, [params]);

  return useQuery({
    queryKey,
    queryFn: async () => {
      const token = getAuthToken();
      if (!token) {
        throw new Error("No authentication token");
      }

      logDebug("Google Calendar Query Function Called", {
        selectedCalendars: params.selectedCalendars,
        googleCalendarEmails,
        googleCalendarEmailsLength: googleCalendarEmails.length,
        willFetchMultiple: googleCalendarEmails.length > 0,
      });

      // If specific Google calendars are selected, fetch each one in parallel
      if (googleCalendarEmails.length > 0) {
        const fetchPromises = googleCalendarEmails.map(async (email) => {
          const queryParams = buildQueryParams({
            email,
            monthsAgo: params.monthsAgo,
            startDate: params.startDate,
            endDate: params.endDate,
          });

          try {
            const response = await apiGet(
              ENDPOINTS.calendar.google,
              token,
              queryParams
            );

            return Array.isArray(response) ? response : [];
          } catch (error: any) {
            if (isCalendarNotConnectedError(error)) {
              logWarning(
                "Google Calendar",
                "Calendar not connected for email:",
                email
              );
              return [];
            }
            throw error;
          }
        });

        const results = await Promise.all(fetchPromises);
        const normalizedEvents = normalizeMultipleCalendarEvents(
          results,
          googleCalendarEmails,
          "google"
        );

        return normalizedEvents;
      }

      // If selectedCalendars was provided but no Google emails matched, return empty array
      if (params.selectedCalendars) {
        logDebug("Google Calendar", {
          message:
            "selectedCalendars provided but no Google emails matched, returning empty array",
        });
        return [];
      }

      // Default behavior: fetch all Google calendars
      const queryParams = buildQueryParams({
        email: params.email,
        monthsAgo: params.monthsAgo,
        loadFirstAccountOnly: params.loadFirstAccountOnly,
        loadSecondAccountOnly: params.loadSecondAccountOnly,
        startDate: params.startDate,
        endDate: params.endDate,
      });

      logCalendarQueryRequest("google", ENDPOINTS.calendar.google, queryParams, token);

      try {
        const response = await apiGet(
          ENDPOINTS.calendar.google,
          token,
          queryParams
        );

        logCalendarQueryResponse("google", response);

        const calendarEmail = params.email || user?.email || undefined;
        const normalizedEvents = normalizeGoogleCalendarEvents(
          response,
          calendarEmail
        );

        logNormalizedCalendarEvents(
          "google",
          response.length,
          normalizedEvents,
          calendarEmail
        );

        return normalizedEvents;
      } catch (error: any) {
        if (isCalendarNotConnectedError(error)) {
          const errorMessage = getErrorMessage(
            error,
            "Calendar not connected"
          );
          logWarning(
            "Google Calendar",
            `Not connected for email: ${params.email}`,
            errorMessage
          );
          return [];
        }

        logError(
          "Google Calendar",
          "Error:",
          JSON.stringify(formatErrorResponse(error), null, 2)
        );
        throw error;
      }
    },
    enabled,
    staleTime: QUERY_CONFIG.calendar.staleTime,
    gcTime: QUERY_CONFIG.calendar.gcTime,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
};

/**
 * Get Microsoft Calendar Events
 * Supports fetching from multiple Microsoft calendars in parallel
 */
export const useMicrosoftCalendarData = (params: CalendarDataParams = {}) => {
  const { getAuthToken, user } = useAuth();
  const { data: accountStatus } = useAccountStatus();

  // Memoize the enabled condition to prevent infinite loops
  const enabled = useMemo(() => {
    const token = getAuthToken();
    const hasMicrosoftAccount = accountStatus && accountStatus.microsoft;
    return !!token && hasMicrosoftAccount;
  }, [getAuthToken, accountStatus]);

  // Parse selected calendars to get Microsoft calendar emails
  const microsoftCalendarEmails = useMemo(() => {
    return parseMicrosoftCalendarEmails(params.selectedCalendars, accountStatus);
  }, [params.selectedCalendars, accountStatus]);

  // Create stable query key with selected calendars
  const queryKey = useMemo(() => {
    return queryKeys.calendar.microsoftWithParams(params);
  }, [params]);

  return useQuery({
    queryKey,
    queryFn: async () => {
      const token = getAuthToken();
      if (!token) throw new Error("No authentication token");

      // If specific Microsoft calendars are selected, fetch each one in parallel
      if (microsoftCalendarEmails.length > 0) {
        const fetchPromises = microsoftCalendarEmails.map(async (email) => {
          const queryParams = buildQueryParams({
            email,
            monthsAgo: params.monthsAgo,
            startDate: params.startDate,
            endDate: params.endDate,
          });

          try {
            const response = await apiGet(
              ENDPOINTS.calendar.microsoft,
              token,
              queryParams
            );

            return Array.isArray(response) ? response : [];
          } catch (error: any) {
            if (isCalendarNotConnectedError(error)) {
              logWarning(
                "Microsoft Calendar",
                "Calendar not connected for email:",
                email
              );
              return [];
            }
            throw error;
          }
        });

        const results = await Promise.all(fetchPromises);
        const normalizedEvents = normalizeMultipleCalendarEvents(
          results,
          microsoftCalendarEmails,
          "microsoft"
        );

        return normalizedEvents;
      }

      // If selectedCalendars was provided but no Microsoft emails matched, return empty array
      if (params.selectedCalendars) {
        logDebug("Microsoft Calendar", {
          message:
            "selectedCalendars provided but no Microsoft emails matched, returning empty array",
        });
        return [];
      }

      // Default behavior: fetch all Microsoft calendars
      const queryParams = buildQueryParams({
        email: params.email,
        monthsAgo: params.monthsAgo,
        startDate: params.startDate,
        endDate: params.endDate,
      });

      try {
        const response = await apiGet(
          ENDPOINTS.calendar.microsoft,
          token,
          queryParams
        );

        const calendarEmail = params.email || user?.email || undefined;
        const normalizedEvents = normalizeMicrosoftCalendarEvents(
          response,
          calendarEmail
        );

        logNormalizedCalendarEvents(
          "microsoft",
          response.length,
          normalizedEvents,
          calendarEmail
        );

        return normalizedEvents;
      } catch (error: any) {
        if (isCalendarNotConnectedError(error)) {
          const errorMessage = getErrorMessage(
            error,
            "Calendar not connected"
          );
          logWarning(
            "Microsoft Calendar",
            `Not connected for email: ${params.email}`,
            errorMessage
          );
          return [];
        }

        logError(
          "Microsoft Calendar",
          "Error:",
          JSON.stringify(formatErrorResponse(error), null, 2)
        );
        throw error;
      }
    },
    enabled,
    staleTime: QUERY_CONFIG.calendar.staleTime,
    gcTime: QUERY_CONFIG.calendar.gcTime,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    // Do not retry when API returns 422 CALENDAR_NOT_CONNECTED — treat as permanent for this session
    retry: (failureCount, error) => !isCalendarNotConnectedError(error),
  });
};

/**
 * Get External Google Events
 */
export const useExternalGoogleEvents = (params: CalendarDataParams = {}) => {
  const { getAuthToken } = useAuth();
  const { data: accountStatus } = useAccountStatus();

  return useQuery({
    queryKey: queryKeys.calendar.externalGoogleWithParams(params),
    queryFn: async () => {
      const token = getAuthToken();
      if (!token) throw new Error("No authentication token");

      const queryParams = buildQueryParams({ email: params.email });

      const response = await apiGet(
        ENDPOINTS.calendar.googleExternal,
        token,
        queryParams
      );

      return response as CalendarEvent[];
    },
    enabled:
      !!getAuthToken() &&
      accountStatus &&
      (accountStatus.google.primary || accountStatus.google.secondary),
    staleTime: QUERY_CONFIG.externalEvents.staleTime,
    gcTime: QUERY_CONFIG.externalEvents.gcTime,
  });
};

/**
 * Get External Microsoft Events
 */
export const useExternalMicrosoftEvents = (params: CalendarDataParams = {}) => {
  const { getAuthToken } = useAuth();
  const { data: accountStatus } = useAccountStatus();

  return useQuery({
    queryKey: queryKeys.calendar.externalMicrosoftWithParams(params),
    queryFn: async () => {
      const token = getAuthToken();
      if (!token) throw new Error("No authentication token");

      const queryParams = buildQueryParams({ email: params.email });

      const response = await apiGet(
        ENDPOINTS.calendar.microsoftExternal,
        token,
        queryParams
      );

      return response as CalendarEvent[];
    },
    enabled:
      !!getAuthToken() && accountStatus && accountStatus.microsoft,
    staleTime: QUERY_CONFIG.externalEvents.staleTime,
    gcTime: QUERY_CONFIG.externalEvents.gcTime,
  });
};

/**
 * Get Combined Calendar Data (Google + Microsoft)
 * Combines data from both calendar sources
 */
export const useCombinedCalendarData = (params: CalendarDataParams = {}) => {
  const { enabled = true, ...calendarParams } = params;
  const { data: accountStatus } = useAccountStatus();
  const { getAuthToken } = useAuth();

  // Only enable individual queries if accounts are configured
  const hasGoogleAccount =
    accountStatus &&
    (accountStatus.google.primary || accountStatus.google.secondary);
  const hasMicrosoftAccount = accountStatus && accountStatus.microsoft;

  const googleQuery = useGoogleCalendarData({
    ...calendarParams,
    enabled: enabled && hasGoogleAccount,
  });
  const microsoftQuery = useMicrosoftCalendarData({
    ...calendarParams,
    enabled: enabled && hasMicrosoftAccount,
  });
  const isProfessionalEmailCalendarEnabled =
    process.env.NEXT_PUBLIC_ENABLE_PROFESSIONAL_EMAIL_CALENDAR === "true" ||
    (typeof window !== "undefined" &&
      window.location.pathname.startsWith("/crm/calendar"));

  const professionalEmailQueryEnabled =
    enabled &&
    isProfessionalEmailCalendarEnabled &&
    !!getAuthToken() &&
    (!calendarParams.selectedCalendars || calendarParams.selectedCalendars.length > 0);

  const professionalEmailQuery = useQuery({
    queryKey: ["calendar-events", "professional-email", calendarParams],
    queryFn: async (): Promise<CalendarEvent[]> => {
      const token = getAuthToken();
      if (!token) {
        throw new Error("No authentication token");
      }

      const queryParams = buildQueryParams({
        email: calendarParams.email,
        monthsAgo: calendarParams.monthsAgo,
        startDate: calendarParams.startDate,
        endDate: calendarParams.endDate,
      });

      const response = await apiGet(
        ENDPOINTS.calendar.professionalEmail,
        token,
        queryParams
      );
      if (!Array.isArray(response)) return [];

      type ProfessionalEmailApiEvent = {
        id?: string;
        summary?: string;
        description?: string;
        location?: string;
        start?: { dateTime?: string };
        end?: { dateTime?: string };
        source?: string;
        allDay?: boolean;
      };

      return (response as ProfessionalEmailApiEvent[]).map((event) => ({
        id: String(event.id || ""),
        title: event.summary || "Untitled Event",
        description: event.description || "",
        start: event.start?.dateTime || "",
        end: event.end?.dateTime || "",
        // Distinct from Google/MS (which use mailbox email as source) so legend + filters stay correct
        source: WTC_EMAIL_CALENDAR_SOURCE_ID,
        location: event.location || "",
        allDay: Boolean(event.allDay),
        provider: "professional-email-calendar" as const,
      }));
    },
    enabled: professionalEmailQueryEnabled,
    staleTime: QUERY_CONFIG.calendar.staleTime,
    gcTime: QUERY_CONFIG.calendar.gcTime,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  // Track if queries are refetching and log when refetch starts
  const isRefetching =
    googleQuery.isRefetching ||
    microsoftQuery.isRefetching ||
    professionalEmailQuery.isRefetching;
  const wasRefetching = useRef(false);
  const prevSelectedIds = useRef(params.selectedCalendars || "");

  useEffect(() => {
    const selectedIds = params.selectedCalendars || "";

    // Log when selected calendars change
    if (selectedIds !== prevSelectedIds.current) {
      logDebug("Calendar Query Selected Calendars Changed", {
        previousIds: prevSelectedIds.current,
        newIds: selectedIds,
        willRefetch: true,
      });
      prevSelectedIds.current = selectedIds;
    }

    // Log when refetch actually starts
    if (isRefetching && !wasRefetching.current) {
      logDebug("Calendar Query Refetch In Progress", {
        selectedIds,
        googleRefetching: googleQuery.isRefetching,
        microsoftRefetching: microsoftQuery.isRefetching,
      });
    }
    wasRefetching.current = isRefetching;
  }, [
    isRefetching,
    params.selectedCalendars,
    googleQuery.isRefetching,
    microsoftQuery.isRefetching,
  ]);

  // Combine the data from both queries
  // Individual calendar failures should not block the display of other calendars
  const combinedData = {
    data: (() => {
      const calendarData: CalendarEvent[] = [];

      if (googleQuery.data) {
        calendarData.push(...googleQuery.data);
      }

      if (microsoftQuery.data) {
        calendarData.push(...microsoftQuery.data);
      }
      if (professionalEmailQuery.data) {
        calendarData.push(...professionalEmailQuery.data);
      }

      return calendarData;
    })(),
    isLoading: googleQuery.isLoading || microsoftQuery.isLoading || professionalEmailQuery.isLoading,
    // Only report error if ALL queries failed or if we have no data and at least one query errored
    isError: (googleQuery.isError && microsoftQuery.isError && professionalEmailQuery.isError) ||
             (googleQuery.data === undefined && microsoftQuery.data === undefined && professionalEmailQuery.data === undefined &&
              (googleQuery.isError || microsoftQuery.isError || professionalEmailQuery.isError)),
    // Only report error if we have no data from any source
    error: (googleQuery.data === undefined && microsoftQuery.data === undefined && professionalEmailQuery.data === undefined) ?
           (googleQuery.error || microsoftQuery.error || professionalEmailQuery.error) :
           null,
    isRefetching:
      googleQuery.isRefetching || microsoftQuery.isRefetching || professionalEmailQuery.isRefetching,
  };

  // Debug log: only log when data actually changes
  useEffect(() => {
    if (!combinedData.data || combinedData.data.length === 0) return;

    const eventsBySource = countEventsBySource(combinedData.data);

    logCombinedCalendarData(
      combinedData.data.length,
      eventsBySource,
      googleQuery.data?.length || 0,
      (microsoftQuery.data?.length || 0) + (professionalEmailQuery.data?.length || 0)
    );
  }, [
    combinedData.data,
    googleQuery.dataUpdatedAt,
    microsoftQuery.dataUpdatedAt,
    professionalEmailQuery.dataUpdatedAt,
  ]);

  return {
    ...combinedData,
    isLoading:
      googleQuery.isLoading || microsoftQuery.isLoading || professionalEmailQuery.isLoading,
    // Only report error if we have no data from any source
    error: (googleQuery.data === undefined && microsoftQuery.data === undefined && professionalEmailQuery.data === undefined) ?
           (googleQuery.error || microsoftQuery.error || professionalEmailQuery.error) :
           null,
    // Expose individual calendar errors for UI warnings
    calendarErrors: {
      google: googleQuery.isError ? googleQuery.error : null,
      microsoft: microsoftQuery.isError ? microsoftQuery.error : null,
      professionalEmail: professionalEmailQuery.isError ? professionalEmailQuery.error : null,
    },
    /** WTC Email (mail app) fetch metadata for unified calendar legend / diagnostics */
    wtcEmailCalendar: {
      queryEnabled: professionalEmailQueryEnabled,
      isFetched: professionalEmailQuery.isFetched,
      isLoading: professionalEmailQuery.isLoading,
      isError: professionalEmailQuery.isError,
      eventCount: professionalEmailQuery.data?.length ?? 0,
    },
    refetch: async () => {
      const results = await Promise.allSettled([
        googleQuery.refetch(),
        microsoftQuery.refetch(),
        professionalEmailQuery.refetch(),
      ]);
      return results;
    },
  };
};
